import React from 'react'

import { Modal, ArrowRight } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, oneOf } from 'prop-types'

import { NOOP } from 'Utils/stableDefaults'

import ProgramFeaturesList from './ProgramFeaturesList'

const PREFIX = 'my_subscriptions.join_modal.'

const INTRO_INSET_KEYS = [
  `${PREFIX}intro_inset_models`,
  `${PREFIX}intro_inset_earnings`,
  `${PREFIX}intro_inset_opportunities`,
]

const INTRO_PROGRAMS = [
  ['intro_programs_essentials_label', 'intro_programs_essentials_text'],
  ['intro_programs_enterprise_label', 'intro_programs_enterprise_text'],
  ['intro_programs_revenue_label', 'intro_programs_revenue_text'],
  ['intro_programs_equity_label', 'intro_programs_equity_text'],
  ['intro_programs_other_label', 'intro_programs_other_text'],
]

const BENEFITS = [
  { labelKey: `${PREFIX}benefit_essentials_label`, textKey: `${PREFIX}benefit_essentials_text` },
  { labelKey: `${PREFIX}benefit_enterprise_label`, textKey: `${PREFIX}benefit_enterprise_text` },
  { labelKey: `${PREFIX}benefit_revenue_label`, textKey: `${PREFIX}benefit_revenue_text` },
  { labelKey: `${PREFIX}benefit_equity_label`, textKey: `${PREFIX}benefit_equity_text` },
  { labelKey: `${PREFIX}benefit_other_label`, textKey: `${PREFIX}benefit_other_text` },
]

const INFO_BOX_KEYS = [
  `${PREFIX}info_box_models`,
  `${PREFIX}info_box_earnings`,
  `${PREFIX}info_box_enterprise`,
]

function JoinProgramModal({
  isOpen,
  isJoining = false,
  mode = 'join',
  redesigned = false,
  onClose,
  onConfirm = NOOP,
  translate,
}) {
  const isInfo = mode === 'info'

  if (!redesigned) {
    const primaryAction = isInfo
      ? {
        content: translate(`${PREFIX}got_it_button`),
        buttonProps: { type: 'primary', onClick: onClose },
      }
      : {
        content: translate(`${PREFIX}confirm_button`),
        buttonProps: { type: 'primary', onClick: onConfirm, disabled: isJoining },
      }

    const secondaryAction = isInfo
      ? undefined
      : {
        content: translate(`${PREFIX}cancel_button`),
        buttonProps: { onClick: onClose, disabled: isJoining },
      }

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        header={translate(`${PREFIX}title_legacy`)}
        canClose
        customClass="join-program-modal join-program-modal--legacy"
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      >
        <div>
          <p className="join-program-modal__description">
            {translate(`${PREFIX}description`)}
          </p>

          <ul className="join-program-modal__list">
            {BENEFITS.map(({ labelKey, textKey }) => (
              <li key={labelKey} className="join-program-modal__list-item cgt-text-medium">
                <span>
                  <strong className="cgt-text-medium--semi-bold">{translate(labelKey)}</strong>
                  {' '}{translate(textKey)}
                </span>
              </li>
            ))}
          </ul>

          <div className="join-program-modal__info-box">
            {isInfo ? (
              <>
                <p className="join-program-modal__info-box-heading">
                  <strong>{translate(`${PREFIX}info_box_essentials_name`)}</strong>
                  {' '}{translate(`${PREFIX}info_box_essentials_suffix`)}
                </p>
                <ProgramFeaturesList
                  translate={translate}
                  className="join-program-modal__info-features"
                />
              </>
            ) : (
              <>
                <h6 className="join-program-modal__info-box-title cgt-heading-100">
                  {translate(`${PREFIX}info_box_title`)}
                </h6>
                <ul className="join-program-modal__list">
                  {INFO_BOX_KEYS.map((key) => (
                    <li key={key} className="join-program-modal__list-item cgt-text-medium">
                      <span>{translate(key)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {isInfo && (
            <div className="join-program-modal__what-happens">
              <h6 className="join-program-modal__what-happens-title cgt-heading-100">
                {translate(`${PREFIX}info_box_title`)}
              </h6>
              <ul className="join-program-modal__list">
                {INFO_BOX_KEYS.map((key) => (
                  <li key={key} className="join-program-modal__list-item cgt-text-medium">
                    <span>{translate(key)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Modal>
    )
  }

  const primaryAction = isInfo
    ? {
      content: translate(`${PREFIX}got_it_button`),
      buttonProps: { type: 'primary', size: 'small', onClick: onClose },
    }
    : {
      content: translate(`${PREFIX}join_cta`),
      buttonProps: {
        type: 'primary',
        size: 'small',
        onClick: onConfirm,
        disabled: isJoining,
        trailIcon: <ArrowRight width="16" height="16" />,
      },
    }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={translate(`${PREFIX}title`)}
      canClose
      customClass="join-program-modal"
      primaryAction={primaryAction}
    >
      <div>
        {isInfo ? (
          <div className="join-program-modal__participating cgt-body-md-semibold">
            {translate(`${PREFIX}participating_pill`)}
          </div>
        ) : (
          <div className="join-program-modal__intro">
            <h6 className="join-program-modal__intro-heading cgt-heading-50">
              {translate(`${PREFIX}intro_heading`)}
            </h6>
            <p className="join-program-modal__intro-body cgt-body-md">
              {translate(`${PREFIX}intro_body`)}
            </p>

            <ul className="join-program-modal__list">
              {INTRO_PROGRAMS.map(([labelKey, textKey]) => (
                <li key={labelKey} className="join-program-modal__list-item cgt-body-base">
                  <strong>{translate(`${PREFIX}${labelKey}`)}</strong> {translate(`${PREFIX}${textKey}`)}
                </li>
              ))}
            </ul>

            <div className="join-program-modal__intro-inset">
              <h6 className="join-program-modal__intro-inset-title cgt-heading-100">
                {translate(`${PREFIX}intro_inset_title`)}
              </h6>
              <ul className="join-program-modal__list">
                {INTRO_INSET_KEYS.map((key) => (
                  <li key={key} className="join-program-modal__list-item cgt-body-base">
                    <span>{translate(key)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <section className="join-program-modal__section">
          <h6 className="join-program-modal__section-title cgt-heading-50">
            {translate(`${PREFIX}section_what_title`)}
          </h6>
          <p className="join-program-modal__section-body cgt-body-base">
            {translate(`${PREFIX}section_what_body`)}
          </p>
        </section>

        <section className="join-program-modal__section">
          <h6 className="join-program-modal__section-title cgt-heading-50">
            {translate(`${PREFIX}section_earnings_title`)}
          </h6>
          <p className="join-program-modal__section-body cgt-body-base">
            {translate(`${PREFIX}section_earnings_body`)}
          </p>
          <p className="join-program-modal__section-body cgt-body-base">
            {translate(`${PREFIX}section_earnings_rule`)}
          </p>
          <p className="join-program-modal__section-body cgt-body-base">
            {translate(`${PREFIX}section_earnings_timing`)}
          </p>
        </section>

        <section className="join-program-modal__section">
          <h6 className="join-program-modal__section-title cgt-heading-50">
            {translate(`${PREFIX}section_refunds_title`)}
          </h6>
          <p className="join-program-modal__section-body cgt-body-base">
            {translate(`${PREFIX}section_refunds_body`)}
          </p>
        </section>

        <section className="join-program-modal__section">
          <h6 className="join-program-modal__section-title cgt-heading-50">
            {translate(`${PREFIX}section_optin_title`)}
          </h6>
          <p className="join-program-modal__section-body cgt-body-base">
            {translate(`${PREFIX}section_optin_body`)}
          </p>
        </section>
      </div>
    </Modal>
  )
}

JoinProgramModal.propTypes = {
  isOpen: bool.isRequired,
  isJoining: bool,
  mode: oneOf(['join', 'info']),
  redesigned: bool,
  onClose: func.isRequired,
  onConfirm: func,
  translate: func.isRequired,
}

export default JoinProgramModal
